/**
 * Tipos y llamada REALES de `consola/auditoria` (`apps/api/src/auditoria/auditoria.controller.ts`
 * + `auditoria.service.ts`). Mismo criterio que `verificacionApi.ts`, `incidentesApi.ts` y
 * `posicionesApi.ts`: estos tipos NO son los de `dominio/contratos.ts` — son DISTINTOS a
 * propósito, y otra vez por la misma causa.
 *
 * 🔴 **El mock `EntradaAuditoria` de `dominio/contratos.ts` NO COINCIDE con lo que el backend
 * real devuelve, y la discrepancia NO es cosmética: es de ALCANCE.** Leído de
 * `auditoria.service.ts` (cuya propia cabecera la declara) + `prisma/schema.prisma`:
 *
 * ```
 *                  MOCK (EntradaAuditoria)                 REAL (este endpoint)
 * actorRol         RolOperacion (rol de CONSOLA:           actorTipo (PASAJERO·CONDUCTOR·
 *                  operador·administrador)                  SISTEMA·OPERADOR) — otra dimensión
 * accion           texto libre ("Suspendió cuenta")        derivada: "BUSCANDO → ASIGNADO"
 * entidad/entidadId cualquier entidad ("Conductor",…)      SIEMPRE un viaje: sólo `viajeId`
 * actorNombre      string                                  string | null
 * ```
 *
 * 🔴 **Y lo más importante para quien lea esta pantalla: el endpoint audita SOLO transiciones de
 * VIAJE.** No existe hoy en el schema ninguna tabla de auditoría genérica multi-entidad — sólo
 * `EventoViaje`. Suspender un conductor, aprobar un documento o publicar un tarifario **no dejan
 * una línea acá**: actualizan la fila directo, sin bitácora histórica. El texto de ayuda de la
 * pantalla mock decía *"toda acción que cambia algo deja una línea acá"* y **eso era falso**
 * contra este backend; `Auditoria.tsx` lo corrigió en el copy, no acá.
 *
 * ⚠️ **NO hay campos `Decimal` en esta respuesta** — se verificó contra el schema antes de
 * tipar, porque la trampa de `Prisma.Decimal → string` sobre HTTP ya mordió en
 * `verificacionApi.ts` (`reputacion`) e `incidentesApi.ts` (`lat`/`lon`). `EventoViaje` no tiene
 * ninguno; lo único que cambia de tipo al serializar es `ocurridoAt` (`Date` → ISO string), y
 * está tipado como `string` abajo.
 *
 * ⚠️ **Grado de verificación, declarado (política del repo: "verificado" es literal):** el
 * contrato de abajo está leído del `service` y del `DTO`, y está ejercitado por el test de
 * integración HTTP del backend (`auditoria.integration.spec.ts`, que corre el controller real con
 * los guards reales). **NO** hubo un `fetch` desde el bundle de la consola contra un `apps/api`
 * vivo — mismo límite que ya declaran `clienteApi.ts` y `posicionesApi.ts`.
 */
import { obtener } from './clienteApi';

/** Los cuatro valores REALES de `ActorTipo` (Prisma). NO es el rol de consola del mock. */
export type ActorTipoAuditoria = 'PASAJERO' | 'CONDUCTOR' | 'SISTEMA' | 'OPERADOR';

/**
 * Los nueve valores REALES de `EstadoViaje` (Prisma). Se declaran completos aunque la pantalla
 * hoy sólo los muestre dentro de la cadena `accion` ya armada por el backend: tener el tipo
 * cerrado es lo que hace que agregar un estado nuevo al schema rompa la compilación acá en vez
 * de aparecer como texto suelto sin traducir.
 */
export type EstadoViajeReal =
  | 'SOLICITADO'
  | 'BUSCANDO'
  | 'ASIGNADO'
  | 'EN_ORIGEN'
  | 'EN_CURSO'
  | 'FINALIZADO'
  | 'CALIFICADO'
  | 'CANCELADO'
  | 'SIN_CONDUCTOR';

/** Una fila de `GET /consola/auditoria`. Espejo exacto de `EntradaAuditoriaViaje` serializada. */
export interface EntradaAuditoriaViajeReal {
  id: string;
  /** 🔴 HORA DE SERVIDOR (R-07), ISO. `Date` en el backend → string sobre HTTP. */
  ocurridoAt: string;
  /** La entidad auditada es SIEMPRE un viaje: no hay `entidad`/`entidadId` genéricos. */
  viajeId: string;
  actorTipo: ActorTipoAuditoria;
  /** `null` cuando el actor es el propio reloj (`SISTEMA`). */
  actorId: string | null;
  /** `null` si `actorId` es `null`, **o si el `User` referenciado ya no existe** (dato histórico). */
  actorNombre: string | null;
  /** `null` en el primer evento de un viaje (no venía de ningún estado). */
  estadoAnterior: EstadoViajeReal | null;
  estadoNuevo: EstadoViajeReal;
  /** Derivada por el backend: `"<anterior|inicio> → <nuevo>"`. No se re-arma acá. */
  accion: string;
  motivo: string | null;
}

/**
 * Filtros del endpoint (`ConsultarAuditoriaDto`). Los cinco son opcionales.
 *
 * ⚠️ **`hasta` es EXCLUSIVO** (`lt` en el service, ejercitado por su test): un evento que ocurre
 * exactamente en `hasta` **queda afuera**. No es un detalle: un rango escrito como
 * `[dia, dia+1)` incluye el día entero, uno escrito como `[dia, dia]` no incluye nada.
 *
 * ⚠️ **`viajeId` tiene que ser un UUID** (`@IsUUID`): cualquier otra cosa la rechaza el backend
 * con 400, no la ignora. Por eso la pantalla NO manda su cuadro de búsqueda por acá.
 *
 * ⚠️ **`limite` está acotado a `[1, 200]`** y `desplazamiento` a `>= 0`: fuera de rango es 400.
 * Sin `limite`, el backend usa **100** (`LIMITE_DEFECTO`) — o sea que la respuesta SIEMPRE está
 * paginada aunque nadie pida paginar, y una pantalla que no lo diga muestra "la auditoría"
 * cuando en realidad muestra las últimas 100 líneas.
 */
export interface FiltroAuditoria {
  /** ISO 8601 (`@IsISO8601`). Inclusivo (`gte`). */
  desde?: string;
  /** ISO 8601 (`@IsISO8601`). **Exclusivo** (`lt`). */
  hasta?: string;
  /** UUID. Cualquier otro formato ⇒ 400. */
  viajeId?: string;
  /** `[1, 200]`. Default del backend: 100. */
  limite?: number;
  /** `>= 0`. Default del backend: 0. */
  desplazamiento?: number;
}

/**
 * El default del backend (`LIMITE_DEFECTO`), replicado acá **para poder MOSTRARLO**, no para
 * cambiarlo: la pantalla necesita saber de cuántas filas es una página para poder decir "estás
 * viendo las N más recientes" y para paginar. Se manda explícito en cada llamada así el número
 * que la pantalla anuncia y el que el backend aplica son el mismo — si sólo se omitiera, los dos
 * podrían divergir el día que el backend cambie su default y nadie se enteraría.
 */
export const LIMITE_AUDITORIA = 100;

/** Ordenado por `ocurridoAt` DESC — el más reciente primero (lo hace el backend, no la pantalla). */
export const consultarAuditoria = (
  filtro: FiltroAuditoria = {},
): Promise<EntradaAuditoriaViajeReal[]> => {
  const parametros = new URLSearchParams();
  if (filtro.desde) parametros.set('desde', filtro.desde);
  if (filtro.hasta) parametros.set('hasta', filtro.hasta);
  if (filtro.viajeId) parametros.set('viajeId', filtro.viajeId);
  if (filtro.limite !== undefined) parametros.set('limite', String(filtro.limite));
  if (filtro.desplazamiento !== undefined)
    parametros.set('desplazamiento', String(filtro.desplazamiento));

  const cadena = parametros.toString();
  return obtener<EntradaAuditoriaViajeReal[]>(
    `/consola/auditoria${cadena === '' ? '' : `?${cadena}`}`,
  );
};
